import React, { useState, useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Card,
  CardContent,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Alert,
  CircularProgress,
  Divider,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import 'dayjs/locale/es';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RTooltip,
  Legend,
  ResponsiveContainer,
  BarChart,
  Bar,
} from 'recharts';

import {
  fetchReporteSeguimientoServicio,
  ReporteSeguimientoServicio,
} from '../../../api/reuniones';
import { useClientes } from '../../../hooks/useClientes';
import { SERVICIOS_RECURRENTES, getServicioNombre } from '../../../hooks/useReuniones';

dayjs.extend(utc);
dayjs.extend(timezone);
const TZ = 'Europe/Madrid';

export default function SeguimientoServicioPage() {
  const { t } = useTranslation<'translation'>();
  const { rows: clientes = [] } = useClientes();

  const [alumnoId, setAlumnoId] = useState<number | ''>('');
  const [servicioId, setServicioId] = useState<number>(SERVICIOS_RECURRENTES.APOYO_PSICOLOGICO);
  const [fechaDesde, setFechaDesde] = useState<Dayjs | null>(null);
  const [fechaHasta, setFechaHasta] = useState<Dayjs | null>(null);

  const {
    data: reporte,
    isLoading,
    error,
  } = useQuery<ReporteSeguimientoServicio>({
    queryKey: ['reporteSeguimientoServicio', alumnoId, servicioId, fechaDesde, fechaHasta],
    queryFn: () =>
      fetchReporteSeguimientoServicio(
        Number(alumnoId),
        servicioId,
        fechaDesde?.toISOString(),
        fechaHasta?.toISOString(),
      ),
    enabled: !!alumnoId && !!servicioId,
    staleTime: 1000 * 60 * 5,
  });

  const handleGenerar = () => {
    // El query se ejecuta automáticamente cuando cambian los parámetros
  };

  const formatFecha = (fecha: string | Date) => {
    return dayjs(fecha).tz(TZ).locale('es').format('DD/MM/YYYY HH:mm');
  };

  const chartData = useMemo(() => {
    if (!reporte?.tendencia) return [];
    return reporte.tendencia.map((item) => ({
      mes: `${item.mes}/${item.ano}`,
      total: item.total,
      realizadas: item.realizadas,
      canceladas: item.canceladas,
    }));
  }, [reporte]);

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="es">
      <Box p={3}>
        <Typography variant="h5" sx={{ mb: 3 }}>
          Reporte de Seguimiento por Servicio
        </Typography>

        {/* Filtros */}
        <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
          <Stack spacing={3}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel>Alumno</InputLabel>
                  <Select
                    value={alumnoId}
                    label="Alumno"
                    onChange={(e) => setAlumnoId(e.target.value as number | '')}
                  >
                    {clientes.map((cliente) => (
                      <MenuItem key={cliente.id} value={cliente.id}>
                        {cliente.nombreCompleto || `Cliente ${cliente.id}`}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel>Servicio</InputLabel>
                  <Select
                    value={servicioId}
                    label="Servicio"
                    onChange={(e) => setServicioId(Number(e.target.value))}
                  >
                    <MenuItem value={SERVICIOS_RECURRENTES.APOYO_PSICOLOGICO}>
                      {getServicioNombre(SERVICIOS_RECURRENTES.APOYO_PSICOLOGICO)}
                    </MenuItem>
                    <MenuItem value={SERVICIOS_RECURRENTES.COUNSELING}>
                      {getServicioNombre(SERVICIOS_RECURRENTES.COUNSELING)}
                    </MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={2}>
                <DatePicker
                  label="Desde"
                  value={fechaDesde}
                  onChange={(newValue) => setFechaDesde(newValue)}
                  slotProps={{ textField: { fullWidth: true } }}
                />
              </Grid>

              <Grid item xs={12} md={2}>
                <DatePicker
                  label="Hasta"
                  value={fechaHasta}
                  onChange={(newValue) => setFechaHasta(newValue)}
                  slotProps={{ textField: { fullWidth: true } }}
                />
              </Grid>
            </Grid>

            <Button
              variant="contained"
              onClick={handleGenerar}
              disabled={!alumnoId || !servicioId}
              sx={{
                backgroundColor: '#21a598',
                '&:hover': { backgroundColor: '#1a8578' },
                maxWidth: 200,
              }}
            >
              Generar Reporte
            </Button>
          </Stack>
        </Paper>

        {/* Contenido del reporte */}
        {isLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            Error al cargar el reporte: {(error as Error).message}
          </Alert>
        )}

        {reporte && !isLoading && (
          <>
            {/* Resumen de métricas */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} md={3}>
                <Card>
                  <CardContent>
                    <Typography color="textSecondary" gutterBottom>
                      Total Reuniones
                    </Typography>
                    <Typography variant="h4">{reporte.metricas.totalReuniones}</Typography>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={3}>
                <Card>
                  <CardContent>
                    <Typography color="textSecondary" gutterBottom>
                      Realizadas
                    </Typography>
                    <Typography variant="h4" color="success.main">
                      {reporte.metricas.realizadas}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      {reporte.metricas.tasaRealizacion.toFixed(1)}%
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={3}>
                <Card>
                  <CardContent>
                    <Typography color="textSecondary" gutterBottom>
                      Canceladas
                    </Typography>
                    <Typography variant="h4" color="error.main">
                      {reporte.metricas.canceladas}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      {reporte.metricas.tasaCancelacion.toFixed(1)}%
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={3}>
                <Card>
                  <CardContent>
                    <Typography color="textSecondary" gutterBottom>
                      Frecuencia Promedio
                    </Typography>
                    <Typography variant="h4">
                      {reporte.metricas.frecuenciaPromedioDias
                        ? `${reporte.metricas.frecuenciaPromedioDias} días`
                        : '—'}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            {/* Información de primera y última reunión */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
              {reporte.primeraReunion && (
                <Grid item xs={12} md={6}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Primera Reunión
                      </Typography>
                      <Typography variant="body2">
                        <strong>Fecha:</strong> {formatFecha(reporte.primeraReunion.fechaInicio)}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Estado:</strong>{' '}
                        <Chip
                          label={reporte.primeraReunion.estado}
                          size="small"
                          color={
                            reporte.primeraReunion.estado === 'realizada' ? 'success' : 'default'
                          }
                        />
                      </Typography>
                      <Typography variant="body2">
                        <strong>Staff:</strong> {reporte.primeraReunion.staffNombre}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Canal:</strong> {reporte.primeraReunion.canal}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              )}

              {reporte.ultimaReunion && (
                <Grid item xs={12} md={6}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Última Reunión
                      </Typography>
                      <Typography variant="body2">
                        <strong>Fecha:</strong> {formatFecha(reporte.ultimaReunion.fechaInicio)}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Estado:</strong>{' '}
                        <Chip
                          label={reporte.ultimaReunion.estado}
                          size="small"
                          color={
                            reporte.ultimaReunion.estado === 'realizada' ? 'success' : 'default'
                          }
                        />
                      </Typography>
                      <Typography variant="body2">
                        <strong>Staff:</strong> {reporte.ultimaReunion.staffNombre}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Canal:</strong> {reporte.ultimaReunion.canal}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              )}
            </Grid>

            {/* Gráfico de tendencia */}
            {chartData.length > 0 && (
              <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Tendencia por Mes
                </Typography>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="mes" />
                    <YAxis />
                    <RTooltip />
                    <Legend />
                    <Bar dataKey="total" fill="#8884d8" name="Total" />
                    <Bar dataKey="realizadas" fill="#82ca9d" name="Realizadas" />
                    <Bar dataKey="canceladas" fill="#ff7c7c" name="Canceladas" />
                  </BarChart>
                </ResponsiveContainer>
              </Paper>
            )}

            {/* Tabla de reuniones */}
            <Paper elevation={2} sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Listado de Reuniones
              </Typography>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>ID</TableCell>
                      <TableCell>Fecha</TableCell>
                      <TableCell>Estado</TableCell>
                      <TableCell>Staff</TableCell>
                      <TableCell>Canal</TableCell>
                      <TableCell>En Cadena</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {reporte.reuniones.map((reunion) => (
                      <TableRow key={reunion.id}>
                        <TableCell>{reunion.id}</TableCell>
                        <TableCell>{formatFecha(reunion.fechaInicio)}</TableCell>
                        <TableCell>
                          <Chip
                            label={reunion.estado}
                            size="small"
                            color={reunion.estado === 'realizada' ? 'success' : 'default'}
                          />
                        </TableCell>
                        <TableCell>{reunion.staffNombre}</TableCell>
                        <TableCell>{reunion.canal}</TableCell>
                        <TableCell>
                          {reunion.idReunionAnterior || reunion.idReunionSiguiente ? (
                            <Chip label="Sí" size="small" color="primary" variant="outlined" />
                          ) : (
                            '—'
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </>
        )}

        {!reporte && !isLoading && !error && (
          <Alert severity="info">Selecciona un alumno y servicio para generar el reporte</Alert>
        )}
      </Box>
    </LocalizationProvider>
  );
}
